import type { CmmdTaskConversation } from "@cmmd-forge/contracts";

import { MarkdownContent } from "~/components/rich-content/MarkdownContent";
import { Button } from "~/components/ui/button";
import { RESPONSIVE_TEXT_BUTTON_CLASS } from "~/lib/touchTarget";
import { formatTaskDateTime } from "./taskDateTime";

export function ProjectTaskConversationTranscript(props: {
  readonly conversation: CmmdTaskConversation;
  readonly loadingMore: boolean;
  readonly error: string | null;
  readonly onLoadMore: () => void;
}) {
  return (
    <div className="border-t border-border bg-muted/30 px-3 py-3">
      {transcriptEvents(props.conversation).length > 0 ? (
        <ol className="space-y-3">
          {transcriptEvents(props.conversation).map((event) => (
            <li key={event.eventId} className="grid gap-1 md:grid-cols-[6.5rem_minmax(0,1fr)]">
              <div className="text-xs font-medium text-muted-foreground">
                {eventLabel(event.kind)}
              </div>
              <div className="min-w-0 text-sm text-foreground">
                {event.text !== null ? (
                  <MarkdownContent variant="compact" allowImages={false}>
                    {event.text}
                  </MarkdownContent>
                ) : (
                  <span className="text-muted-foreground">
                    {event.status ?? "Activity recorded"}
                  </span>
                )}
                <time
                  className="mt-1 block text-xs text-muted-foreground"
                  dateTime={event.occurredAt}
                >
                  {formatTaskDateTime(event.occurredAt)}
                </time>
              </div>
            </li>
          ))}
        </ol>
      ) : (
        <p className="text-sm text-muted-foreground">No finalized messages were stored.</p>
      )}
      {props.error !== null ? (
        <p role="alert" className="mt-3 text-sm text-destructive">
          {props.error}
        </p>
      ) : null}
      {props.conversation.nextCursor !== null ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className={`mt-3 ${RESPONSIVE_TEXT_BUTTON_CLASS}`}
          disabled={props.loadingMore}
          onClick={props.onLoadMore}
        >
          {props.loadingMore ? "Loading…" : "Load older messages"}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * The events a reader sees. A Recorded turn's usage event carries tokens for
 * the usage ledger and no text, so it is not a transcript line.
 */
function transcriptEvents(conversation: CmmdTaskConversation) {
  return conversation.events.filter((event) => event.kind !== "turn_usage");
}

function eventLabel(kind: CmmdTaskConversation["events"][number]["kind"]): string {
  switch (kind) {
    case "user_message":
      return "You";
    case "assistant_message":
      return "Agent";
    case "activity_summary":
      return "Activity";
    case "turn_status":
      return "Status";
    case "error_summary":
      return "Error";
    case "tool_call":
      return "Tool";
    case "turn_usage":
      return "Usage";
  }
}
